import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { useCatalogItemPreview } from "@/context/cms-puck/CatalogItemPreviewContext";
import type { SelectionItem } from "@/lib/cms-puck/blocks/selection";
// `@/pages/dashboard/artwork-overview` résout vers le fichier frère
// `artwork-overview.tsx`, qui ne réexporte que le default.
import ArtworkPage from "@/pages/dashboard/artwork-overview";
import MultipleprintOverviewPage from "@/pages/dashboard/multipleprint-overview";

/**
 * Aperçu canvas du bloc « Fiche (catalogue raisonné) ».
 *
 * Ne réimplémente RIEN : monte les fiches d'aperçu du back-office
 * (`artwork-overview` / `multipleprint-overview`) en mode `embedded`. Ces deux
 * pages sont elles-mêmes le miroir de `ArtworkPage` / `MultipleprintPage` du
 * front-public (mêmes composants d'en-tête, d'overview et d'accordéons, mêmes
 * compute-fields) : l'éditeur affiche donc la même maquette que le site publié,
 * et une future évolution de la fiche n'a plus qu'un seul endroit à suivre
 * côté BO au lieu de deux.
 *
 * Une version précédente redessinait la fiche à la main ici et avait silencieusement
 * dérivé (ancienne maquette d'en-tête : titre et n° d'enregistrement côte à côte).
 */
export function CatalogueItemDetailPreview({
  item,
}: {
  item?: SelectionItem | null;
}) {
  const cmsNew = useCmsNew();
  const { itemId, itemType } = useCatalogItemPreview();
  // Sans œuvre choisie dans le picker : première œuvre / multiple du catalogue,
  // pour que le canvas montre toujours la fiche (le site lit l'item dans l'URL).
  const selected: SelectionItem | undefined = item?.id
    ? item
    : itemId && itemType
      ? { type: itemType, id: itemId }
      : undefined;

  if (!selected) {
    return (
      <div className="flex w-full flex-col items-center justify-center rounded border border-dashed border-slate-300 py-12 text-sm text-slate-500">
        <p className="font-medium text-slate-700">
          {cmsNew("cms.new.auto.text.fiche_detail_catalogue", "Fiche détail catalogue")}
        </p>
        <p className="mt-2 max-w-md px-4 text-center text-xs text-amber-700">
          {cmsNew(
            "cms.new.preview.itemDetail.selectHint",
            "Sélectionnez une œuvre ou un multiple dans le picker pour l'aperçu. Sur le site publié, l'élément vient de l'URL.",
          )}
        </p>
      </div>
    );
  }

  if (selected.type === "artwork") {
    return (
      <div className="w-full">
        <p className="mb-2 px-5 text-xs cms-page-text-muted">
          {cmsNew(
            "cms.new.preview.itemDetail.artworkHint",
            "Aperçu éditeur avec l'œuvre sélectionnée. Sur le site, l'élément vient de l'URL.",
          )}
        </p>
        <ArtworkPage artworkId={selected.id} embedded />
      </div>
    );
  }

  if (selected.type === "multipleprint") {
    return (
      <div className="w-full">
        <p className="mb-2 px-5 text-xs cms-page-text-muted">
          {cmsNew(
            "cms.new.preview.itemDetail.multipleHint",
            "Aperçu éditeur avec le multiple sélectionné. Sur le site, l'élément vient de l'URL.",
          )}
        </p>
        <MultipleprintOverviewPage multipleprintId={selected.id} embedded />
      </div>
    );
  }

  return (
    <div className="w-full py-12 text-center text-sm cms-page-text-muted">
      {cmsNew("cms.new.preview.itemDetail.notFound", "Élément introuvable ou non publié.")}
    </div>
  );
}
